<script setup lang="ts">
import { ref, watch, computed, onMounted, reactive } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useMemberService } from "@/stores/memberFunction/memberService";

import DefaultImage from "@image/memberFunction/11/pankcard01_bg.png";
import triangle_icon from "@image/memberFunction/common/triangle_icon.png";
import 文字框_長_A from "@image/memberFunction/文字框/文字框_長_A.png";
import seveneleven_logo from "@image/memberFunction/12/7eleven-logo.png";
import family_Logo from "@image/memberFunction/12/family-Logo.png";
import HiLife_Logo from "@image/memberFunction/12/HiLife-Logo.png";
import OK_Logo from "@image/memberFunction/12/OK-Logo.png";
import phone_code from "@image/memberFunction/07/phone_code.png";
import { useLogin } from "@/stores/login";

interface AddStoreDepositFormData {
  sProtocol: string;
  sUsdtAddr: string;
  nVscode: string;
  sFile0: Blob;
}

const AddStoreForm = ref<any>(null);
// const storeName = ref<string[]>(["TRC-20", "ERC-20"]);
const storeName = ref<string[]>(["TRC-20"]);
const emit = defineEmits(["changeDepositPage"]);
const login = useLogin();
const memberService = useMemberService();
const { t } = useI18n();
const { isSendMsg, UsdtExchangeRate } = storeToRefs(memberService);
const File0ImageInput = ref<HTMLInputElement | null>(null);
const Images = reactive({
  defaultFile0Image: DefaultImage,
});
const ImagesState = reactive({
  File0ImageState: false,
});
const Msg = ref<string>("");

function changeDepositPage(page: number) {
  emit("changeDepositPage", page);
}
function submit(VForm: AddStoreDepositFormData) {
  Msg.value = "";
  if (!File0ImageInput.value?.files?.[0]) {
    Msg.value = t("MemberFunction.Deposit.CheckWalletAddressScreenshot");
    return;
  }

  AddStoreForm.value.resetForm();
  memberService.getAddMemberUSDT({
    ...VForm,
    sFile0: File0ImageInput.value.files[0] as Blob,
  });
  clearInput();
  changeDepositPage(8);
}

function handleFileUpload(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = (target.files as FileList)[0];
  if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
    const reader = new FileReader();

    reader.onload = async (e) => {
      if (!e.target) return (Images.defaultFile0Image = "");
      if (!e.target.result) return (Images.defaultFile0Image = "");
      Images.defaultFile0Image = e.target.result.toString();
      ImagesState.File0ImageState = true;
    };
    reader.readAsDataURL(file);
  } else {
    Images.defaultFile0Image = "";
    ImagesState.File0ImageState = false;
    alert(t("MemberFunction.Deposit.SelectCorrectImage"));
  }
}

function clearInput() {
  if (!File0ImageInput.value) return;

  File0ImageInput.value.value = "";
  Images.defaultFile0Image = DefaultImage;
  ImagesState.File0ImageState = false;
}

const phoneNumber = ref<string>("");
const sendPhoneMsgError = ref<string>("");
async function Phonebtn() {
  sendPhoneMsgError.value = '';
  try {
    const res = await memberService.USDTGetPhoneMailCheck();
    if (res && res.type !== "0") {
      sendPhoneMsgError.value = res.msg;
      return;
    }
  } catch (error) {
    console.error("Error sending phone verification:", error);
  }
}

onMounted(() => {
  if (login.memberData.user_phone) {
    phoneNumber.value = login.memberData.user_phone;
  } else {
    phoneNumber.value = "";
  }
})

</script>
<template>
  <p class="text-[18px] font-bold text-center">
    {{ $t("MemberFunction.Deposit.BindUSDTWallet") }}
  </p>
  <VForm @submit="submit" ref="AddStoreForm" class="px-[9vw]">
    <div class="text-primary">
      <label for="sProtocol" class="grid grid-cols-12 text-[14px] md:text-[16px] mt-[16px] md:mt-[3dvh]">
        <p class="mr-[1em] col-span-4">
          {{ $t("MemberFunction.Deposit.WalletCategory") }}
        </p>
        <div class="relative col-span-8">
          <div class="h-[26px] w-full">
            <img v-img-src="triangle_icon" alt=""
              class="h-[12px] absolute right-[2.5%] md:right-[3.4%] top-[16%] z-20" />
          </div>
          <VField id="sProtocol" :label="$t('MemberFunction.Deposit.PaymentMethod')" name="sProtocol" v-slot="{ field }"
            class="relative" :rules="{ required: true }">
            <select v-bind="field"
              class="absolute select-bg-border top-0 bg-transparent px-2 rounded w-full h-[26px] text-primary">
              <option class="text-black" value="">
                {{ $t("MemberFunction.Deposit.WalletCategoryPlaceholder") }}
              </option>
              <option class="text-black" v-for="value in storeName" :key="value + 'onlineDepositOther'" :value="value">
                {{ value }}
              </option>
            </select>
          </VField>
          <ErrorMessage name="sProtocol" class="text-primary text-[10px]" v-slot="{ message }">
            <p class="text-primary text-[10px] mr-[6px]">
              {{
                message.includes("必填")
                  ? $t("MemberFunction.Deposit.Require")
                  : message
              }}
            </p>
          </ErrorMessage>
        </div>
      </label>
      <label for="sUsdtAddr" class="grid grid-cols-12 text-[14px] md:text-[16px] mt-[16px] md:mt-[3dvh]">
        <p class="mr-[1em] col-span-4">
          {{ $t("MemberFunction.Deposit.WalletAddress") }}
        </p>
        <div class="relative inline-block col-span-8">
          <div class="h-[26px]">
            <img v-img-src="文字框_長_A" alt="" class="h-[26px] w-full" />
          </div>
          <VField class="col-span-1 absolute h-[26px] top-0 rounded-md bg-transparent w-full px-2" id="sUsdtAddr"
            :label="$t('MemberFunction.Deposit.DepositAmount')" name="sUsdtAddr" type="string"
            :rules="{ required: true }" />
          <ErrorMessage name="sUsdtAddr" class="text-primary text-[10px]" v-slot="{ message }">
            <p class="text-primary text-[10px] mr-[6px]">
              {{
                message.includes("必填")
                  ? $t("MemberFunction.Deposit.Require")
                  : message
              }}
            </p>
          </ErrorMessage>
        </div>
      </label>
      <label for="File0" class="grid grid-cols-12 text-[14px] md:text-[16px] mt-[16px] md:mt-[3dvh]">
        <p class="mr-[1em] col-span-4">
          {{ $t("MemberFunction.Deposit.WalletAddressScreenshot") }}
        </p>
        <div class="relative bg-black col-span-8 rounded-[6px]">
          <button type="button" class="absolute top-[6px] right-[6px] bg-white/50 text-secondary rounded-full z-20"
            @click="clearInput" v-if="ImagesState.File0ImageState">
            <p class="relative -translate-y-[4px] w-[20px] h-[20px]">
              x
            </p>
          </button>
          <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-full h-full"
            v-show="!ImagesState.File0ImageState">
            <p class="text-primary text-[30px] text-center flex justify-center items-center h-full">
              +
            </p>
            <input ref="File0ImageInput" id="File0" name="sFile0" type="file"
              class="absolute inset-0 opacity-0 cursor-pointer" accept=".png, .jpg, .jpeg"
              @change="handleFileUpload" />
          </div>
          <div class="relative overflow-hidden rounded-[6px]">
            <img v-img-src="DefaultImage" alt="" class="relative" />
            <img v-if="ImagesState.File0ImageState" :src="Images.defaultFile0Image" alt=""
              class="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 z-10" />
            <img v-else v-img-src="Images.defaultFile0Image" alt=""
              class="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 z-10" />
          </div>
        </div>
      </label>
      <p class="text-primary text-[10px] mr-[6px]">
        {{ Msg }}
      </p>
      <label for="sPhone" class="grid grid-cols-12 text-[14px] md:text-[16px] mt-[16px] md:mt-[3dvh] relative">
        <p class="mr-[1em] col-span-4">
          {{ $t("MemberFunction.Deposit.PhoneNumber") }}
        </p>
        <div class="relative inline-block col-span-8">
          <div class="relative">
            <div class="h-[26px]">
              <img v-img-src="文字框_長_A" alt="" class="h-[26px] w-full" />
            </div>
            <VField class="col-span-1 absolute h-[26px] top-0 rounded-md bg-transparent w-full px-2" id="sPhone"
              :label="$t('MemberFunction.Deposit.PhoneNumberPlaceholder')" name="sPhone" type="string"
              v-model="phoneNumber" :rules="{ required: true }" disabled="true" />
            <div class="flex justify-center items-center absolute top-1/2 -translate-y-1/2 right-0">
              <button @click="Phonebtn" type="button" :class="{ grayscale: !isSendMsg }" :disabled="!isSendMsg">
                <div class="relative">
                  <img class="w-[70px]" v-img-src="phone_code" alt="" />
                  <p
                    class="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 text-[12px] w-[100%] leading-[10px] text-black font-bold">
                    {{ $t("LoginView.GetVerification") }}
                  </p>
                </div>
              </button>
            </div>
          </div>

          <p v-if="sendPhoneMsgError !== ''" class="text-primary text-[10px] mr-[6px]">
            {{ sendPhoneMsgError }}
          </p>
        </div>
      </label>
      <label for="nVscode" class="grid grid-cols-12 text-[14px] md:text-[16px] mt-[16px] md:mt-[3dvh]">
        <p class="mr-[1em] col-span-4">
          {{ $t("MemberFunction.Deposit.Verification") }}
        </p>
        <div class="relative inline-block col-span-8">
          <div class="h-[26px]">
            <img v-img-src="文字框_長_A" alt="" class="h-[26px] w-full" />
          </div>
          <VField class="col-span-1 absolute h-[26px] top-0 rounded-md bg-transparent w-full px-2" id="nVscode"
            :label="$t('MemberFunction.Deposit.VerificationPlaceholder')" name="nVscode" type="string"
            :rules="{ required: true }" />
          <ErrorMessage name="nVscode" class="text-primary text-[10px]" v-slot="{ message }">
            <p class="text-primary text-[10px] mr-[6px]">
              {{
                message.includes("必填")
                  ? $t("MemberFunction.Deposit.VerificationPlaceholder")
                  : message
              }}
            </p>
          </ErrorMessage>
        </div>
      </label>
    </div>

    <div class="mt-[3dvh] md:static flex flex-col justify-center items-center">
      <button type="submit" class="btn-y-border">
        <p class="text-[14px] md:text-[14px] font-bold text-secondary px-[2em] py-[.1em]">
          {{ $t("MemberFunction.Deposit.Submit") }}
        </p>
      </button>
      <div class="mt-[3dvh]">
        <div class="flex justify-center items-center leading-[36px]">
          <img src="@image/memberFunction/11/驚嘆號icon.png" alt="" class="w-[16px] mr-1" />
          <span class="text-red-600">
            {{ $t("MemberFunction.Deposit.StoreNotice") }}
          </span>
        </div>
        <div class="text-primary text-[14px] md:text-[16px] leading-[20px]">
          <div class="mb-[1vh] flex">
            <p class="text-red-600 mr-1">※</p>
            <p>
              {{ $t("MemberFunction.Deposit.USDT2NoticeList1") }}
              {{ UsdtExchangeRate }} TWD
            </p>
          </div>
          <div class="mb-[1vh] flex">
            <p class="text-red-600 mr-1">※</p>
            <p>{{ $t("MemberFunction.Deposit.USDT2NoticeList2") }}</p>
          </div>
          <div class="mb-[1vh] flex">
            <p class="text-red-600 mr-1">※</p>
            <p>
              {{ $t("MemberFunction.Deposit.USDT2NoticeList3") }}
            </p>
          </div>
          <div class="mb-[1vh] flex">
            <p class="text-red-600 mr-1">※</p>
            <p>
              {{ $t("MemberFunction.Deposit.USDT2NoticeList4") }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </VForm>
</template>
